import React from 'react'
import { format, parseISO } from 'date-fns'
import { object } from 'prop-types'

import Grid from '@/component/Primitive/Grid'
import Card from '@/component/Primitive/Card'
import Surround from '@/component/Primitive/Surround'
import Tag from '@/component/Primitive/Tag'
import CardButton from '@/component/Primitive/Card/component/CardButton'
import changeFileExtension from '@/lib/filename-extension-modifier'

const LatestMagazineIssueWidget = ({ magazineIssue }) => {
  if (!magazineIssue) return null

  const formattedMagazine = {
    title: format(parseISO(magazineIssue.issueDate), 'MMMM yyyy'),
    imageRatio: 446 / 321,
    to: '/magazine',
    link: `/magazine/${magazineIssue.slug}`,
    src: magazineIssue.images.cover_321x446_321[0].url,
    srcSet: [
      {
        src: changeFileExtension(
          magazineIssue.images.cover_321x446_321[0].url,
          'webp'
        ),
        width: 321
      },
      {
        src: changeFileExtension(
          magazineIssue.images.cover_321x446_642[0].url,
          'webp'
        ),
        width: 642
      }
    ],
    sizes: [
      '(min-width: 1440px) 356px',
      // calc(((50vw - 64px - 24px) * 0.55) - 32px)'
      // content gutter (64px) | padding between cards (24px) | percentage width of card (0.55) | padding within card (2 * 16px)
      '(min-width: 960px) calc(((50vw - 88x) * 0.55) - 32px)',
      'calc(100vw - 48px)'
    ],
    description: magazineIssue.description
  }

  const formatHighlight = (highlight) => ({
    title: highlight.title,
    imageRatio: 241 / 322,
    to: `/magazine?page=${highlight.pageNumber}`,
    src: highlight.images.main_141x106_322[0].url,
    srcSet: [
      {
        src: highlight.images.main_141x106_322[0].url,
        width: 322
      },
      {
        src: highlight.images.main_141x106_644[0].url,
        width: 644
      },
      {
        src: highlight.images.main_141x106_900[0].url,
        width: 900
      }
    ],
    link: `/magazine/${magazineIssue.slug}?page=${highlight.pageNumber}`,
    category: highlight.pageNumber
  })

  const heroHighlights = magazineIssue.highlights
    .slice(0, 2)
    .map(formatHighlight)

  const normalHighlights = magazineIssue.highlights
    .slice(2, 4)
    .map(formatHighlight)

  return (
    <Grid gutter="default">
      <Grid.Item hidden="mobile" trailingGap="mobile" width={{ d: 1 / 2 }}>
        <Card
          horizontal
          inset
          gridWidths={[6 / 11, 5 / 11]}
          contentBorder={['top']}
          headerComponents={() => (
            <Tag themedFont background="white">
              Latest Edition
            </Tag>
          )}
          footerComponents={() => <CardButton inverse>Read now</CardButton>}
          {...formattedMagazine}
          inverse
          typeConfig={{
            title: { size: ['heading7', 'heading6'], themed: true },
            description: { size: ['body2', 'body4'] }
          }}
          truncateDescriptionAt={null}
        />
      </Grid.Item>
      <Grid.Item hidden="desktop" trailingGap="mobile" width={{ d: 1 / 2 }}>
        <Card
          inset
          headerComponents={() => <Tag background="white">Latest Edition</Tag>}
          footerComponents={() => (
            <CardButton block inverse>
              Read now
            </CardButton>
          )}
          {...formattedMagazine}
          inverse
          typeConfig={{
            title: { size: ['heading4', 'heading6'], themed: true },
            description: { size: ['body2', 'body4'] }
          }}
        />
      </Grid.Item>
      <Grid.Item width={{ d: 1 / 2 }}>
        <Grid gutter="default">
          {heroHighlights.map((item, i) => (
            <Grid.Item
              trailingGap="default"
              key={i}
              width={{ d: 1 / 2 }}
              hidden={i === 1 ? 'mobile' : null}
            >
              <Surround bordered="default" padded="default">
                <Card
                  footerComponents={() => <Tag>Page {item.category}</Tag>}
                  {...item}
                  typeConfig={{ title: { themed: true } }}
                />
              </Surround>
            </Grid.Item>
          ))}
          {normalHighlights.map((item, i) => (
            <Grid.Item
              trailingGap={i < normalHighlights.length - 1 ? 'mobile' : null}
              key={i}
              width={{ d: 1 / 2 }}
            >
              <Surround
                bordered={i < normalHighlights.length - 1 ? 'mobile' : null}
                padded={i < normalHighlights.length - 1 ? 'mobile' : null}
              >
                <Card
                  horizontal
                  gridWidths={[5 / 11, 6 / 11]}
                  footerComponents={() => <Tag>Page {item.category}</Tag>}
                  typeConfig={{ title: { themed: true } }}
                  {...item}
                />
              </Surround>
            </Grid.Item>
          ))}
        </Grid>
      </Grid.Item>
    </Grid>
  )
}

LatestMagazineIssueWidget.propTypes = {
  magazineIssue: object
}

export default LatestMagazineIssueWidget
